/**
 * @file DeviceStore.jsx
 * @description Global device state management using React Context + useReducer.
 *              Polls the backend every 10 seconds for the latest device data and
 *              normalises telemetry payloads for easier UI consumption.
 */
import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { io } from 'socket.io-client';
import deviceManagementService from '../services/deviceManagementService';
import mewpService from '../services/mewpService';

// Initial State
const initialState = {
    devices: [],
    tracking: [],
    loading: true,
    error: null,
    lastUpdated: null,
};

// Actions
const ACTIONS = {
    SET_DEVICES: 'SET_DEVICES',
    SET_DATA: 'SET_DATA',
    SET_ERROR: 'SET_ERROR',
    SET_LOADING: 'SET_LOADING',
    LIVE_FRAME: 'LIVE_FRAME', // real-time MQTT frame pushed over Socket.IO
};

// Reducer
const deviceReducer = (state, action) => {
    switch (action.type) {
        case ACTIONS.SET_DEVICES:
            return {
                ...state,
                devices: action.payload,
                loading: false,
                error: null,
                lastUpdated: new Date(),
            };
        case ACTIONS.SET_DATA:
            return {
                ...state,
                devices: action.payload.devices,
                tracking: action.payload.tracking,
                loading: false,
                error: null,
                lastUpdated: new Date(),
            };
        case ACTIONS.SET_ERROR:
            return { ...state, error: action.payload, loading: false };
        case ACTIONS.SET_LOADING:
            return { ...state, loading: true };
        case ACTIONS.LIVE_FRAME: {
            // Merge a single real-time frame into the matching device so the live
            // status updates instantly — no waiting for the next 10s poll.
            const f = action.payload;
            if (!f || f.device_id == null) return state;
            const id = String(f.device_id);
            let matched = false;
            const devices = state.devices.map(d => {
                if (String(d.device_id) !== id) return d;
                matched = true;
                if (f.heartbeat) {
                    // Device is transmitting but this frame has no usable telemetry:
                    // keep it "fresh" so it doesn't wrongly flip to Vehicle Off.
                    return { ...d, __liveTs: f.last_seen, last_seen: f.last_seen };
                }
                return {
                    ...d,
                    ...(f.payload || {}), // fresh CAN values
                    __liveTs: f.last_seen,
                    last_seen: f.last_seen,
                    live_status: f.status,
                    live_engine_on: f.engineOn,
                    live_ign_on: f.ignOn,
                    live_gps_on: f.gpsOn,
                    engine_speed: f.engine_speed != null ? f.engine_speed : d.engine_speed,
                    ign: f.ign != null ? f.ign : d.ign,
                    lat: f.lat != null ? f.lat : d.lat,
                    lng: f.lng != null ? f.lng : d.lng,
                    location: {
                        ...(d.location || {}),
                        lat: f.lat, lon: f.lng, latitude: f.lat, longitude: f.lng,
                        ign: f.ign != null ? f.ign : (d.location && d.location.ign),
                    },
                };
            });
            return matched ? { ...state, devices } : state;
        }
        default:
            return state;
    }
};

// Context
export const DeviceContext = createContext();

// Provider
export const DeviceProvider = ({ children }) => {
    const [state, dispatch] = useReducer(deviceReducer, initialState);

    const fetchDevices = async () => {
        try {
            // Fetch both in parallel to reduce window of inconsistency and total request time
            const [deviceRes, trackingRes] = await Promise.allSettled([
                deviceManagementService.getUniqueDevices(),
                mewpService.getAllTracking()
            ]);

            const devicesRaw = deviceRes.status === 'fulfilled' 
                ? (deviceRes.value.devices || deviceRes.value.data || deviceRes.value || []) 
                : [];
            
            const trackingRaw = trackingRes.status === 'fulfilled'
                ? (trackingRes.value.data || trackingRes.value || [])
                : [];

            // Flatten Telemetry for easier UI Consumption (from latest_telemetry)
            const normalizedDevices = Array.isArray(devicesRaw) ? devicesRaw.map(d => ({
                ...d,
                ...(d.latest_telemetry || {})
            })) : [];

            dispatch({ 
                type: ACTIONS.SET_DATA, 
                payload: { 
                    devices: normalizedDevices, 
                    tracking: Array.isArray(trackingRaw) ? trackingRaw : [] 
                } 
            });
        } catch (error) {
            console.error('Failed to fetch devices/tracking:', error);
            dispatch({
                type: ACTIONS.SET_ERROR,
                payload: error.message || 'Failed to connect to server'
            });
        }
    };

    useEffect(() => {
        fetchDevices();
        const interval = setInterval(fetchDevices, 10000); // 10s poll (reconciler + drives staleness re-eval)
        return () => clearInterval(interval);
    }, []);

    // Real-time telemetry: push frames straight from MQTT over Socket.IO, so the
    // live status reflects the broker the instant data arrives (independent of the
    // poll above, which now just reconciles state and ages out silent devices).
    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) return;

        const apiBaseUrl = process.env.REACT_APP_API_URL || 'https://api.mtandtiot.in/api';
        const socketUrl = apiBaseUrl.replace(/\/api\/?$/, '');

        const socket = io(socketUrl, {
            auth: { token },
            path: '/socket.io',
            transports: ['polling', 'websocket'],
        });

        socket.on('connect', () => socket.emit('subscribe_telemetry'));
        socket.io.on('reconnect', () => socket.emit('subscribe_telemetry'));
        socket.on('device_telemetry', (frame) => {
            dispatch({ type: ACTIONS.LIVE_FRAME, payload: frame });
        });

        return () => {
            try {
                socket.emit('unsubscribe_telemetry');
                socket.off('device_telemetry');
                socket.disconnect();
            } catch (e) { /* ignore */ }
        };
    }, []);

    return (
        <DeviceContext.Provider value={{ state, refresh: fetchDevices }}>
            {children}
        </DeviceContext.Provider>
    );
};

// Custom Hook
export const useDeviceStore = () => {
    const context = useContext(DeviceContext);
    if (!context) {
        throw new Error('useDeviceStore must be used within a DeviceProvider');
    }
    return context;
};
